import * as React from 'react';
import { Box, FormControl, TablePagination, InputLabel, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import SearchIcon from '@mui/icons-material/Search';
import { numberKeys } from 'src/utils/record';
import { GetCompaniesAdminParams, companiesDeletedFilterValues, CompaniesDeletedFilterValuesType } from 'src/dto/Company';
import { CompaniesList } from '../CompaniesList';
import { GetListLimit } from 'src/dto/Limits';
import { useGetCompaniesAdminQuery } from 'src/redux/services/companiesAdmin';
import { GetCompaniesSort } from 'src/dto/Company';
import Row from 'src/frontend-common/components/Row';

import FailureScreen from 'src/frontend-common/components/FailureScreen';
import { Header, SearchInput, SearchIconWrapper, CustomLoadingScreen } from 'src/pages/Companies/components/style';

interface State {
  page: number;
}

export interface Props {
  searchFilter: string | undefined;
  setSearchFilter: React.Dispatch<React.SetStateAction<string | undefined>>;
  limit: GetListLimit;
  setLimit: (i: GetListLimit) => void;
  page?: number;
  setPage?: (s: number) => void;
  sort: GetCompaniesSort;
  setSort: (i: GetCompaniesSort) => void;
}

const limitOptions: Record<GetListLimit, null> = {
  5: null,
  10: null,
  25: null,
  100: null,
};

const sortedLimitOptions = numberKeys(limitOptions).sort((a, b) => a - b);

const pageInitialValue = 0;

export const CompaniesListWrapper: React.FC<Props> = ({
  searchFilter,
  setSearchFilter,
  limit,
  setLimit,
  page: controlledPage,
  setPage: controlledSetPage,
  sort,
  setSort,
}) => {
  const [search, setSearch] = React.useState('');
  const [statePage, stateSetPage] = React.useState<State['page']>(pageInitialValue);
  const [page, setPage] = controlledPage !== undefined && controlledSetPage !== undefined ? [controlledPage, controlledSetPage] : [statePage, stateSetPage];
  const [companiesDeletedFilter, setCompaniesDeletedFilter] = React.useState<CompaniesDeletedFilterValuesType>('Active');

  const [filters, setFilters] = React.useState<GetCompaniesAdminParams & { sort: GetCompaniesSort }>({
    start: pageInitialValue,
    max_items: limit,
    search: searchFilter,
    sort: sort,
  });
  const { data, isLoading, isError } = useGetCompaniesAdminQuery({
    ...filters,
    show_deleted: companiesDeletedFilter === 'Inactive' ? true : false,
  });

  const handleSort = (s: Props['sort']) => {
    setSort(s);
  };
  const callback = (
    page: State['page'],
    limit: Props['limit'],
    sort: Props['sort'],
    search: string | undefined
    // eslint-disable-next-line max-params
  ) => {
    setFilters({
      start: page * limit,
      max_items: limit,
      sort: sort,
      search,
    });
  };

  const debouncedCallback = useDebouncedCallback(callback, 300);

  React.useEffect(() => {
    setPage(pageInitialValue);
    debouncedCallback.callback(pageInitialValue, limit, sort, searchFilter);
  }, [searchFilter, sort]);

  React.useEffect(() => {
    debouncedCallback.callback(page, limit, sort, searchFilter);
  }, [page, limit]);

  const handleChangeCompaniesDeletedFilter = (event: SelectChangeEvent<typeof companiesDeletedFilter>) => {
    setCompaniesDeletedFilter(event.target.value as CompaniesDeletedFilterValuesType);
  };
  return (
    <Box>
      <Header>
        <Row style={{ width: '100%' }}>
          <SearchInput
            disabled={!data}
            id="search-input"
            placeholder="Search companies"
            startAdornment={
              <SearchIconWrapper color="disabled">
                <SearchIcon />
              </SearchIconWrapper>
            }
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                // to prevent search with empty string
                setSearchFilter(search);
              }
            }}
            onChange={(v) => {
              setSearch(v);
            }}
            value={search}
          />
          <FormControl style={{ minWidth: 170, marginLeft: 24 }}>
            <InputLabel>Status</InputLabel>
            <Select value={companiesDeletedFilter} label="Status" sx={{ width: '100%' }} onChange={handleChangeCompaniesDeletedFilter}>
              {companiesDeletedFilterValues.map((filter) => (
                <MenuItem key={filter} value={filter}>
                  {`${filter}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Row>
      </Header>

      {isLoading && <CustomLoadingScreen variant="circle" />}
      {isError && <FailureScreen>An error occurred while fetching the companies, please retry.</FailureScreen>}
      {data && (
        <>
          <div data-testid="loaded-companies-list" />
          <CompaniesList companies={data.items} sort={sort} setSort={handleSort} isShowDeleted={companiesDeletedFilter === 'Inactive'} />
        </>
      )}

      <TablePagination
        component="div"
        count={data ? data.total : 1}
        page={page}
        /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
        onPageChange={(_, p) => {
          setPage(p);
        }}
        rowsPerPageOptions={sortedLimitOptions}
        rowsPerPage={limit}
        onRowsPerPageChange={(l) => {
          setPage(pageInitialValue);
          setLimit(parseInt(l.target.value, 10) as GetListLimit);
        }}
      />
    </Box>
  );
};
